import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DataManager, Query, ODataV4Adaptor, Predicate } from '@syncfusion/ej2-data';
import { SaveEventArgs, EditSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastComponent, ToastCloseArgs, ToastPositionModel, ToastModule } from '@syncfusion/ej2-angular-notifications';
import { HttpEventType, HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-file-attachment',
  templateUrl: './file-attachment.component.html',
  styleUrls: ['./file-attachment.component.scss']
})
export class FileAttachmentComponent implements OnInit, OnChanges {
  // @Input() relatedTypeId: number;
  // @Input() recordId: number;
  @Input() relatedConditions: { relatedTypeId: number; recordId: number }[] = [];
  @Output() closeScopeInfo = new EventEmitter<void>();

  @ViewChild('grid') public grid!: GridComponent;

  @ViewChild('col0Template', { static: true }) col0Template: any;
  @ViewChild('defaulttoast') public toastObj!: ToastComponent;
  @ViewChild('col1Template', { static: true }) col1Template: any;
  @ViewChild('col2Template', { static: true }) col2Template: any;

  public previewImageUrl: SafeUrl | null = null;
  public previewTitle = 'File Preview';
  public previewKind: 'pdf' | 'image' | null = null;
  public previewPdfVisible = false;
  public previewPdfBytes: Uint8Array | null = null;
  public previewImageVisible = false;
  private previewObjectUrl: string | null = null;

  selectedFile!: File;
  data!: DataManager;
  actionFailedError : string = "";
  query!: Query;
  baseUrl: string = environment.baseUrl.replace('/api/', '/odata/');
  editSettings!: EditSettingsModel;
  toolbar!: string[];
  columns!: object[];
  public pageOptions: PageSettingsModel =  { pageSize: 25, pageSizes: [25, 50, 100], pageCount: 5 };
  public submitClicked: boolean = false;
  public formData!: UntypedFormGroup;
  currentUser: any
  uploadProgress: number = 0;
  isUploading: boolean = false;
  public position: ToastPositionModel = { X: "Right" };

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private formBuilder: UntypedFormBuilder,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.editSettings = { allowAdding: true, mode: 'Dialog' };
    this.toolbar = ['Add']

    this.currentUser = this.authenticationService.currentUserValue;
    this.columns = [
      { field: 'Id', headerText: '', isPrimaryKey: true, visible: false },
      { field: 'Id', headerText: 'Action', template: this.col0Template },
      { field: 'Name', headerText: 'Name' },
      { field: 'FileName', headerText: 'File Name', template: this.col1Template },
      { field: 'CreatedBy.FirstName', headerText: 'Created By', template: this.col2Template }
    ]
    this.data = new DataManager({
      url: this.baseUrl + "FileAttachment",
      adaptor: new ODataV4Adaptor(),
      headers: [{ 'Authorization': 'Bearer ' + this.currentUser.token }]
    });

    this.refreshQuery();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['relatedConditions'] && !changes['relatedConditions'].firstChange) {
      this.refreshQuery();
      if (this.grid) {
        this.grid.refresh();
      }
    }
  }

  private refreshQuery(): void {
    const predicate = this.buildRelatedPredicate();
    if (!predicate) {
      this.query = new Query().where('Id', 'equal', -1);
      return;
    }

    this.query = new Query().expand('CreatedBy($select=FirstName)')
      .where(predicate).sortByDesc('CreatedOn');
  }

  private buildRelatedPredicate(): Predicate | null {
    let finalPredicate: Predicate | null = null;
    for (const condition of this.relatedConditions) {
      const predicate = new Predicate('RelatedTypeId', 'equal', condition.relatedTypeId)
        .and('RelatedId', 'equal', condition.recordId);

      finalPredicate = finalPredicate ? finalPredicate.or(predicate) : predicate;
    }
    return finalPredicate;
  }

  actionBegin(args: SaveEventArgs): void {
    let objData: any = args.rowData;
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      this.submitClicked = false;
      const relatedCondition = this.relatedConditions?.[0];

      this.formData = this.formBuilder.group({
        Id: [objData.Id ? objData.Id : 0],
        RelatedTypeId: [objData.RelatedTypeId ? objData.RelatedTypeId : relatedCondition?.relatedTypeId ?? 0],
        RelatedId: [objData.RelatedId ? objData.RelatedId : relatedCondition?.recordId ?? 0],
        FileName: [objData.FileName ? objData.FileName : '', Validators.required],
        Size: [objData.Size ? objData.Size : '', Validators.required],
        Name: [objData.Name ? objData.Name : '', Validators.required]
      });
    }

    if (args.requestType === 'save') {
      this.submitClicked = true;
      if (this.formData.valid && this.selectedFile) {
        const uploadData = new FormData();
        uploadData.append('file', this.selectedFile, this.selectedFile.name);
        uploadData.append('fileAttachment', JSON.stringify(this.formData.value));

        this.isUploading = true;
        this.uploadProgress = 0;
        this.http.post(`${environment.baseUrl}FileAttachment/upload`, uploadData, {
          reportProgress: true,
          observe: 'events'
        }).subscribe(event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round(100 * event.loaded / event.total);
          } else if (event.type === HttpEventType.Response) {
            this.grid.closeEdit();
            this.grid.refresh();
            this.isUploading = false;
          }
        }, error => {
          this.actionFailedError = error;
          this.toastObj.show();
          console.log(error);
          this.isUploading = false;
        });
        args.cancel = true;
      } else {
        args.cancel = true;
      }
    }

  }

  actionComplete(args): void {
    if ((args.requestType === 'beginEdit' || args.requestType === 'add')) {
      if (args.requestType === 'add') {
        (args.form.elements.namedItem('Name') as HTMLInputElement).focus();
        args.dialog.header = 'Add new file';
      }
    }
    if (args.requestType === 'save') {
      args.cancel = true;
    }
  }

  // async actionFailure(args): Promise<void> {
  //   this.submitClicked = false;
  //   // console.error(args);
  //   // // Extract and display the error message from the response.
  //   // const errorMessage = args.error && args.error[0] && args.error[0].error ? args.error[0].error.Message : 'Unknown error occurred';
  //   // console.error(errorMessage);
  //   if(args && args.error && args.error[0] &&
  //     args.error[0].error
  //   )
  //   {
  //     try{
  //       const response = await args.error[0].error.text();
  //       this.actionFailedError = JSON.parse(response).message;
  //       console.log("err"+JSON.parse(response).message);
  //       this.toastObj.show();
  //     }catch(error){
  //       this.actionFailedError = error;
  //       this.toastObj.show();
  //     }
  //   }
  // }

  public onCreate = (): void => {
    setTimeout((): void => {
        this.toastObj.show();
    }, 200);
}

public onClose = (e: ToastCloseArgs): void => {
    if (e.toastContainer.childElementCount === 0) {
    }
}

public onBeforeOpen = (): void => {
}


  get f() {
    return this.formData.controls;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.selectedFile = file;
    if (file) {
      this.formData.patchValue({ FileName: file.name });
      this.formData.patchValue({ Size: this.fileSize(file.size) });
    };
  }

  fileSize(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024,
      dm = decimals <= 0 ? 0 : decimals || 2,
      sizes = ['Bytes', 'KB', 'MB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  close() {
    this.closeScopeInfo.emit();
  }

  canPreview(fileData: any): boolean {
    return this.getPreviewType(fileData?.FileName) !== null;
  }

  downloadFile(fileData: any) {
    let queryApi = environment.baseUrl + `FileAttachment/download/${fileData.Id}`;
    this.http.get(queryApi, { responseType: 'blob', observe: 'response' })
      .subscribe(respone => {
        let fileName = fileData.FileName;
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(respone.body);
        link.download = fileName;
        link.click();
      })
  }

  previewFile(fileData: any): void {
    const previewType = this.getPreviewType(fileData?.FileName);
    if (!previewType) {
      this.downloadFile(fileData);
      return;
    }

    this.clearPreviewUrl();
    this.http.get(environment.baseUrl + `FileAttachment/download/${fileData.Id}`, { responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          this.previewKind = previewType;
          this.previewTitle = fileData?.FileName || 'File Preview';
          if (previewType === 'pdf') {
            blob.arrayBuffer().then((buffer) => {
              this.previewPdfBytes = new Uint8Array(buffer);
              this.previewPdfVisible = true;
            });
            this.previewImageUrl = null;
          } else {
            this.previewObjectUrl = URL.createObjectURL(blob);
            this.previewImageUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);
            this.previewImageVisible = true;
          }
        },
        error: (error) => {
          this.actionFailedError = error?.message || 'Unable to preview file.';
          this.toastObj.show();
        }
      });
  }

  onPreviewClose(): void {
    this.clearPreviewUrl();
    this.previewImageVisible = false;
    this.previewKind = null;
    this.previewTitle = 'File Preview';
  }

  onPdfPreviewClosed(): void {
    this.previewPdfBytes = null;
    this.previewPdfVisible = false;
    this.previewKind = null;
    this.previewTitle = 'File Preview';
  }

  private getPreviewType(fileName?: string): 'pdf' | 'image' | null {
    const normalized = (fileName || '').toLowerCase();
    if (normalized.endsWith('.pdf')) {
      return 'pdf';
    }
    if (/\.(png|jpg|jpeg|gif|bmp|webp|svg)$/.test(normalized)) {
      return 'image';
    }
    return null;
  }

  private clearPreviewUrl(): void {
    this.previewImageUrl = null;
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }
}
